import updateBoxId from '@Services/updateBoxId';
import {
  Box,
  Grid,
  Typography,
  alpha,
  Autocomplete,
  InputLabel,
  FormControl,
  OutlinedInput,
  FormHelperText,
  Card,
  CardContent,
  Chip,
  useTheme,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';
import { ValidationResult } from 'joi';
import React, { useEffect, useState, useMemo } from 'react';
import SbButton from '../../../../components/Button';
import Divider from '../../../../components/Divider';
import SbTextField from '../../../../components/Textfield';
import SBDropdown from '../../../../components/Dropdown';
import { ZoomAdjustedPopper } from '../../../../theme/zoomPopper';
import CreateShipmentContext from '../../../../context/CreateShipment';
import {
  AddProductType,
  BoxType,
  ProductErrorType,
  ProductType,
  createShipmentContextType,
} from '../../../../types/shipment';
import {
  defaultBoxState,
  defaultProductErrorState,
  defaultProductState,
  PRODUCT_ID_TYPE_OPTIONS,
} from '../../constants';
import BoxDimensions from '../BoxDimensions';
import ProductTable from '../ProductTable';
import { getProductDescriptionSchema } from './schema';
import {
  getEuComplianceRule,
  resolveShipmentValueInEur,
  isComplianceRequired,
  isEUCountry,
} from '../../../../utils/euCompliance';
import { useEurExchangeRate } from '../../../../utils/currencyConversion';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isExportShipment, getCountryName } from '../../../../utils/exportShipment';
import { userLogin } from '@Services/hasAdminAccess';
import {
  isUSDestination,
  CPSC_DISCLAIM_CODE_OPTIONS,
  CPSC_DEFAULT_DISCLAIM_CODE,
  normalizeCpscDisclaimCode,
  defaultCpscComplianceState,
  validateCpscCompliance,
  buildCpscPayload,
  parseBoolean,
} from '../../../../utils/usCompliance';
import { UsCpscComplianceType } from '../../../../types/shipment';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { SHIPMENT_TYPES } from '../../../GetRatesV2/Constant';
import { getChargeableWeight } from '@Reducers/GetRates/actions';
import { AddressType } from '@Reducers/GetRates/Type';
import { setMaximumCharegableWeight } from '@Reducers/GetRates';
import { isInTransit } from '../../../../utils/getPreviousBoxDimensions';
import { isActionAllowed } from '../../../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import { useLocation } from 'react-router-dom';
import API from '@Services/apiAxios';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';

interface ProductDescriptionProps {
  writeAccess?: boolean;
}

function getAddressObj({ pincode, city, country, state }: AddressType) {
  return { city, state, country, pincode };
}

const ProductDescription: React.FC<ProductDescriptionProps> = ({ writeAccess }): JSX.Element => {
  const theme = useTheme();
  const {
    selectedBox,
    setSelectedBox,
    boxes,
    setBoxes,
    selectedProduct,
    setSelectedProduct,
    shipmentType,
    consignee,
    shipper,
    setChargeableWeight,
    totalShipmentValue,
    pickupType: { selectedWearhouseAddress },
    setIsProductFormInvalid,
    setIsHSNValidating,
    getHSNCodeFetched,
    setGetHSNCodeFetched,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;
  const [productDetails, setProductDetails] = useState<AddProductType>({ ...defaultProductState });
  const [errors, setErrors] = useState<ProductErrorType>({ ...defaultProductErrorState });
  const { pathname, search } = useLocation();

  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
  const isCreateShipmentFlow =
    normalizedPathname === '/shipment/create-shipment' ||
    normalizedPathname === '/shipment/create-single' ||
    normalizedPathname === '/shipment' ||
    normalizedPathname.startsWith('/shipment/create-bulk');
  const hasReference = search.includes('ref=') && search.includes('accountId=');
  const isInitialCreateMode = isCreateShipmentFlow || hasReference;

  const [isSameAsProductId, setIsSameAsProductId] = useState(false);
  const [isEditMode, setIsEditMode] = useState(isInitialCreateMode);
  const rates = useSelector((state: RootStateType) => state.getRates);
  const { shipmentById } = useSelector((state: RootStateType) => state.adminShipment);

  const { id, description, quantity, unitValue, HSNName, HSNCode } = productDetails;
  const writePermission = writeAccess ?? true;
  const organizationCountry = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '', _country } = organizationData || {};
  const dispatch = useDispatch<AppDispatch>();

  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);
  const { mutlipleShipmentsData } = useSelector((state: RootStateType) => state.trackShipment) || [];
  const checkForIsInTransit = isInTransit(mutlipleShipmentsData?.[0]);
  const isInTransitEditAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Shipment_Details.edit_package_after_in_transit,
    PERFORM_ACTION.write,
  );
  const isDisabled = shipmentById?.isInvoiceGenerated || (checkForIsInTransit && !isInTransitEditAllowed);

  const [isLoadingHSN, setIsLoadingHSN] = useState(false);

  const orgCountryName = _country?.name?.trim() || organizationCountry?._country?.name?.trim() || '';
  const consigneeCountry = getCountryName(consignee);

  const isUserLogin = userLogin();
  const destCountry = consigneeCountry || getCountryName(shipmentById?._consigneeId) || '';
  const isDestEU = isEUCountry(destCountry);
  const { rate: eurRate } = useEurExchangeRate({ currency: shipmentType?.currency, isEU: isDestEU });
  const shipmentValueEur = resolveShipmentValueInEur(boxes, shipmentType.currency, totalShipmentValue, eurRate);
  const { isVisible: showEuComplianceFields, isRequired: requireEuComplianceFields } = getEuComplianceRule({
    destCountry,
    shipmentMode: shipmentType.shipmentMode,
    shipmentValueEur,
    isUserLogin,
  });

  const eurEquivalentText = useMemo(() => {
    if (!isDestEU || !eurRate || (shipmentType?.currency || '').toUpperCase() === 'EUR') return '';
    const eurVal = (totalShipmentValue * eurRate).toFixed(2);
    return `(≈ €${eurVal})`;
  }, [isDestEU, eurRate, shipmentType?.currency, totalShipmentValue]);
  const isEUConsignee = showEuComplianceFields;
  const isUSConsignee = isUSDestination(destCountry);
  const isCpscRequired = isUSConsignee && isComplianceRequired(isUserLogin);
  const isProductIdRequired = isComplianceRequired(isUserLogin);

  useEffect(() => {
    if (!isUSConsignee) {
      setProductDetails(prev => ({
        ...prev,
        cpscCompliance: { ...defaultCpscComplianceState },
      }));
      setErrors(prev => {
        const next = { ...prev };
        delete next.cpscBeingSold;
        delete next.cpscDisclaimCode;
        delete next.cpscAdditionalContext;
        delete next.cpscCertificateFiled;
        delete next.cpscProductIdRegistry;
        delete next.cpscCertificateVersionId;
        delete next.cpscCertifierId;
        delete next.cpscProductId;
        delete next.cpscProductIdType;
        return next;
      });
    }
  }, [isUSConsignee]);

  const isShipmentExport = isExportShipment({
    orgCountryName,
    shipper,
    consignee,
    shipmentById,
  });

  const isGetHSNDisabled =
    !writePermission ||
    isDisabled ||
    !isEditMode ||
    isLoadingHSN ||
    getHSNCodeFetched ||
    (!productDetails.description?.trim() && (!selectedBox?.products || selectedBox.products.length === 0));

  const handleGetHSNCode = async () => {
    const destCountry = getCountryName(shipmentById?._consigneeId) || getCountryName(consignee) || '';

    const items: Array<{
      description: string;
      productId: number;
      isCurrentForm?: boolean;
      boxProductId?: string;
    }> = [];

    // 1. Add other products from the box (excluding the one being edited, to avoid duplicates)
    if (selectedBox?.products) {
      selectedBox.products.forEach(p => {
        if (productDetails.isUpdate && p._id === productDetails._id) {
          return;
        }
        if (p.description?.trim()) {
          items.push({
            description: p.description.trim(),
            productId: Math.floor(Math.random() * 1000000),
            boxProductId: p._id || p.id,
          });
        }
      });
    }

    // 2. Add the current form product
    if (productDetails.description?.trim()) {
      items.push({
        description: productDetails.description.trim(),
        productId: Math.floor(Math.random() * 1000000),
        isCurrentForm: true,
      });
    }

    if (items.length === 0) return;

    try {
      setIsLoadingHSN(true);
      const payload = {
        countryName: destCountry,
        items: items.map(item => ({
          description: item.description,
          productId: item.productId,
        })),
      };

      const response = await API.post('/speedboxbot/hsn', payload);

      if (response.data && !response.data.isError && (response.data.results || response.data.data)) {
        const results = response.data.results || response.data.data || [];
        setGetHSNCodeFetched(true);

        // Helper to find matching result for an item
        const getMatchForProduct = (item: { description: string; productId: number }) => {
          return results.find(
            (r: any) =>
              (r.productId !== undefined && String(r.productId) === String(item.productId)) ||
              (r.description && r.description.toLowerCase().trim() === item.description.toLowerCase().trim()) ||
              (r.suppliedDescription &&
                r.suppliedDescription.toLowerCase().trim() === item.description.toLowerCase().trim()),
          );
        };

        // Helper to map response to classifications
        const mapClassifications = (matchResult: any) => {
          if (!matchResult) return undefined;
          if (Array.isArray(matchResult.hsnCodes)) {
            return matchResult.hsnCodes.map((code: any) => ({
              hsnCode: code.HSNCode || code.hsnCode || '',
              hurricaneDescription: code.HSNName || code.hurricaneDescription || code.hsnName || '',
              confidence: code.confidence,
            }));
          }
          if (Array.isArray(matchResult.classifications)) {
            return matchResult.classifications;
          }
          return undefined;
        };

        // Update the form productDetails if it matches current form item
        const currentFormItem = items.find(i => i.isCurrentForm);
        if (currentFormItem) {
          const match = getMatchForProduct(currentFormItem);
          const classifications = mapClassifications(match);
          if (classifications && classifications.length > 0) {
            setProductDetails(prev => ({
              ...prev,
              classifications,
            }));
          }
        }

        // Update the products in the box
        if (selectedBox?.products?.length) {
          let boxProductsChanged = false;
          const updatedBoxProducts = selectedBox.products.map(p => {
            const correspondingItem = items.find(
              i =>
                (p._id && i.boxProductId === p._id) ||
                (p.id && i.boxProductId === p.id) ||
                (i.isCurrentForm && productDetails.isUpdate && p._id === productDetails._id) ||
                i.description.toLowerCase().trim() === p.description?.toLowerCase().trim(),
            );
            if (correspondingItem) {
              const match = getMatchForProduct(correspondingItem);
              const classifications = mapClassifications(match);
              if (classifications && classifications.length > 0) {
                boxProductsChanged = true;
                return {
                  ...p,
                  classifications,
                };
              }
            }
            return p;
          });

          if (boxProductsChanged) {
            const updatedSelectedBox = {
              ...selectedBox,
              products: updatedBoxProducts,
            };
            setSelectedBox(updatedSelectedBox);

            const updatedBoxes = boxes.map(box => (box.id === selectedBox.id ? updatedSelectedBox : box));
            setBoxes(updatedBoxes);
          }
        }
      } else {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Oops!',
            subTitle: response.data?.msg || response.data?.message || 'Something went wrong.',
          }),
        );
      }
    } catch (error: any) {
      console.error('Error fetching HSN Code:', error);
      const errorMsg =
        error?.response?.data?.msg || error?.response?.data?.message || error?.message || 'Something went wrong.';
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: 'Oops!',
          subTitle: errorMsg,
        }),
      );
    } finally {
      setIsLoadingHSN(false);
    }
  };

  useEffect(() => {
    if (selectedProduct) {
      const rawCpsc = selectedProduct.cpscCompliance || selectedProduct.compliance?.us;
      const cpscCompliance = rawCpsc
        ? {
            beingSold: parseBoolean(rawCpsc.beingSold) ?? true,
            disclaimCode: normalizeCpscDisclaimCode(rawCpsc.disclaimCode),
            additionalContext: rawCpsc.additionalContext || '',
            certificateFiled: parseBoolean(rawCpsc.certificateFiled),
            productIdRegistry: rawCpsc.productIdRegistry || '',
            certificateVersionId: rawCpsc.certificateVersionId || '',
            certifierId: rawCpsc.certifierId || '',
            productId: rawCpsc.productId || '',
            productIdType: rawCpsc.productIdType || 'SKU',
          }
        : { ...defaultCpscComplianceState };

      setProductDetails({
        ...defaultProductState,
        ...selectedProduct,
        cpscCompliance,
      });
    } else {
      setProductDetails({ ...defaultProductState });
    }
    setIsSameAsProductId(false);
  }, [selectedProduct]);

  useEffect(() => {
    if (isInitialCreateMode) {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
    }
  }, [isInitialCreateMode]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    validateProperty(event);
    const name = event.target.name as keyof AddProductType;
    const { value } = event.target;
    const productDataTemp = { ...productDetails };
    if (name === 'description' || name === 'HSNCode' || name === 'HSNName') {
      productDataTemp[name] = value;
      if (name === 'description') {
        productDataTemp.classifications = undefined;
        setGetHSNCodeFetched(false);
      }
    } else if (name === 'quantity') {
      productDataTemp[name] = parseInt(value);
    } else if (name === 'unitValue') {
      productDataTemp[name] = value;
    } else if (name === 'productId') {
      productDataTemp.productId = value;
      if (isSameAsProductId) {
        productDataTemp.merchantProductId = value;
      }
    } else if (name === 'merchantProductId') {
      productDataTemp.merchantProductId = value;
    } else if (name === 'standardManufacturerProductId') {
      productDataTemp.standardManufacturerProductId = value;
    } else if (name === 'nonStandardManufacturerProductId') {
      productDataTemp.nonStandardManufacturerProductId = value;
    } else if (name === 'productIdType') {
      productDataTemp.productIdType = value;
    }
    setProductDetails({ ...productDataTemp });
  }

  const handleSameAsProductIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsSameAsProductId(checked);
    if (checked) {
      setProductDetails(prev => ({
        ...prev,
        merchantProductId: prev.productId || '',
      }));
    }
  };

  const handleCpscBeingSoldChange = (beingSold: boolean) => {
    setProductDetails(prev => {
      const prevCpsc = prev.cpscCompliance || { beingSold: true };
      if (!beingSold) {
        return {
          ...prev,
          cpscCompliance: {
            beingSold: false,
            disclaimCode: normalizeCpscDisclaimCode(prevCpsc.disclaimCode),
            additionalContext: '',
          },
        };
      } else {
        return {
          ...prev,
          cpscCompliance: {
            beingSold: true,
            certificateFiled: undefined,
            productIdRegistry: '',
            certificateVersionId: '',
            certifierId: '',
            productId: prev.productId || '',
            productIdType: prev.productIdType || 'SKU',
          },
        };
      }
    });

    setErrors(prev => {
      const next = { ...prev };
      delete next.cpscBeingSold;
      delete next.cpscDisclaimCode;
      delete next.cpscAdditionalContext;
      delete next.cpscCertificateFiled;
      delete next.cpscProductIdRegistry;
      delete next.cpscCertificateVersionId;
      delete next.cpscCertifierId;
      delete next.cpscProductId;
      delete next.cpscProductIdType;
      return next;
    });
  };

  const handleCpscCertificateFiledChange = (certificateFiled: boolean) => {
    setProductDetails(prev => {
      const prevCpsc = prev.cpscCompliance || { beingSold: true };
      if (certificateFiled) {
        return {
          ...prev,
          cpscCompliance: {
            ...prevCpsc,
            beingSold: true,
            certificateFiled: true,
          },
        };
      } else {
        return {
          ...prev,
          cpscCompliance: {
            ...prevCpsc,
            beingSold: true,
            certificateFiled: false,
            productIdRegistry: '',
            certificateVersionId: '',
            certifierId: '',
          },
        };
      }
    });

    setErrors(prev => {
      const next = { ...prev };
      delete next.cpscCertificateFiled;
      delete next.cpscProductIdRegistry;
      delete next.cpscCertificateVersionId;
      delete next.cpscCertifierId;
      delete next.cpscProductId;
      delete next.cpscProductIdType;
      return next;
    });
  };

  const handleCpscFieldChange = (field: keyof UsCpscComplianceType, value: any) => {
    setProductDetails(prev => ({
      ...prev,
      cpscCompliance: {
        ...(prev.cpscCompliance || { beingSold: true }),
        [field]: value,
      },
    }));

    const errorKey = `cpsc${field.charAt(0).toUpperCase() + field.slice(1)}` as keyof ProductErrorType;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: '',
      }));
    }
  };

  function finalCharegableWTByID() {
    if (shipmentById?.shipmentNumber) {
      const obj = {
        destination: { ...getAddressObj(shipmentById?._consigneeId) },
        // source: { ...getAddressObj(selectedWearhouseAddress ? selectedWearhouseAddress : shipper) },
        source: {
          ...getAddressObj(
            shipmentById?._packageId?.isDropOff ? shipmentById?._warehouseAddressId : shipmentById?._shipperId,
          ),
        },
        package: {
          box: shipmentById?._packageId?.box?.map(box => ({
            weight: box.weight.toString(),
            length: Number(box.length),
            width: Number(box.width),
            height: Number(box.height),
          })),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        _courierId: shipmentById?.sellingPricing?._courierId,
        shipmentEdit: true,
        inbound: shipmentById?.sellingPricing?.inbound,
        outbound: shipmentById?.sellingPricing?.outbound,
        shipmentNumber: shipmentById?.shipmentNumber,
      };
      // console.log(obj);
      dispatch(getChargeableWeight({ ...obj }));
    }
  }

  function updateFinalChargableWeight(organizationName?: boolean) {
    if (shipmentById?.shipmentNumber) {
      const obj = {
        destination: { ...getAddressObj(shipmentById?._consigneeId) },
        // source: { ...getAddressObj(selectedWearhouseAddress ? selectedWearhouseAddress : shipper) },
        source: { ...getAddressObj(shipmentById?._shipperId) },
        package: {
          box: boxes.map(box => ({
            weight: box.weight.toString(),
            length: Number(box.length),
            width: Number(box.width),
            height: Number(box.height),
          })),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        _courierId: shipmentById?.sellingPricing?._courierId,
        shipmentEdit: true,
        shipmentNumber: shipmentById?.shipmentNumber,
        inbound: shipmentById?.sellingPricing?.inbound,
        outbound: shipmentById?.sellingPricing?.outbound,
      };
      dispatch(getChargeableWeight({ ...obj }));
    } else {
      const obj = {
        destination: { ...getAddressObj(consignee) },
        source: {
          ...getAddressObj(selectedWearhouseAddress?.label ? selectedWearhouseAddress : shipper),
        },
        package: {
          box: boxes.map(box => ({
            weight: box.weight.toString(),
            length: Number(box.length),
            width: Number(box.width),
            height: Number(box.height),
          })),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
      };
      dispatch(getChargeableWeight({ ...obj, organizationName }));
    }

    const updateChargeableWeight = rates?.chareableWeight?.maxChargeableWt;

    if (updateChargeableWeight) {
      setChargeableWeight(updateChargeableWeight);
    }
  }

  useEffect(() => {
    if (shipmentById?.buyingPricing) {
      finalCharegableWTByID();
      // setChargeableWeight(shipmentById?.sellingPricing?.chargeableWeight);
      if (shipmentById.sellingPricing?.chargeableWeight)
        dispatch(setMaximumCharegableWeight(shipmentById?.sellingPricing?.chargeableWeight));
    }
  }, [shipmentById?.shipmentNumber, shipmentById?._packageId?.box, shipmentType.unit]);

  function addProduct() {
    const boxesTemp = structuredClone(boxes);
    const productsTemp = structuredClone(selectedBox.products);
    const boxIndex = boxesTemp.findIndex(box => box.id === selectedBox.id);
    const productInd = id ? productsTemp.findIndex(product => product.id === id) : -1;

    // set product index
    const _id = productInd > -1 ? productInd.toString() : boxesTemp[boxIndex].products.length.toString();
    console.log({ boxIndex, productInd, _id });

    const sanitizedProductDetails = {
      ...productDetails,
      unitValue:
        typeof productDetails.unitValue === 'string' ? Number(productDetails.unitValue) : productDetails.unitValue,
      merchantProductId: showEuComplianceFields ? productDetails.merchantProductId?.trim() || '' : '',
      standardManufacturerProductId: productDetails.standardManufacturerProductId?.trim() || 'NO',
      nonStandardManufacturerProductId: showEuComplianceFields
        ? productDetails.nonStandardManufacturerProductId?.trim() || ''
        : '',
      cpscCompliance: isUSConsignee
        ? buildCpscPayload(productDetails.cpscCompliance, isUSConsignee, productDetails)
        : undefined,
    };

    // productsTemp[productInd] = { ...productDetails };
    if (boxesTemp[boxIndex].products.length && productInd > -1) {
      // update selected index
      boxesTemp[boxIndex].products[productInd] = {
        ...sanitizedProductDetails,
        id: _id,
      };
    } else {
      // add new product
      boxesTemp[boxIndex].products = [...boxesTemp[boxIndex].products, { ...sanitizedProductDetails, id: _id }];
    }

    setBoxes([...boxesTemp]);
    setSelectedBox({
      ...selectedBox,
      products: [...boxesTemp[boxIndex].products],
    });
    // setSelectedProduct(null);
    // setSelectedProduct({ ...defaultProductState });
    setSelectedProduct({ ...defaultProductState, id: `${boxesTemp[boxIndex].products.length}` });
    setIsSameAsProductId(false);

    updateFinalChargableWeight();
  }

  function updateProduct(): void {
    // Ensure selectedBox and products are defined
    if (!selectedBox || !selectedBox.products) {
      return;
    }

    // Cast unitValue to number if it's a string and sanitize EU fields
    const updatedProductDetails = {
      ...productDetails,
      unitValue:
        typeof productDetails.unitValue === 'string' ? Number(productDetails.unitValue) : productDetails.unitValue,
      merchantProductId: showEuComplianceFields ? productDetails.merchantProductId?.trim() || '' : '',
      standardManufacturerProductId: productDetails.standardManufacturerProductId?.trim() || 'NO',
      nonStandardManufacturerProductId: showEuComplianceFields
        ? productDetails.nonStandardManufacturerProductId?.trim() || ''
        : '',
      cpscCompliance: isUSConsignee
        ? buildCpscPayload(productDetails.cpscCompliance, isUSConsignee, productDetails)
        : undefined,
    };

    // Find the index of the item with matching id in the products array
    const indexToUpdate = selectedBox.products.findIndex(item => item._id === updatedProductDetails._id);

    // If a matching item is found, update it with the data
    if (indexToUpdate !== -1) {
      // Create a new array with the updated item
      const updatedProducts = [
        ...selectedBox.products.slice(0, indexToUpdate), // Items before the updated item
        updatedProductDetails, // Updated item
        ...selectedBox.products.slice(indexToUpdate + 1), // Items after the updated item
      ];

      // Assign the updated array back to selectedBox
      selectedBox.products = updatedProducts;

      const updateBoxes = boxes?.map(box => (box.id === selectedBox.id ? selectedBox : box));
      setBoxes(updateBoxes);

      setSelectedBox({
        ...selectedBox,
        products: updatedProducts,
      });
      setSelectedProduct({ ...defaultProductState });
      setIsSameAsProductId(false);
      // setSelectedProduct({ ...selectedProduct });
    } else {
      // If no matching item is found, simply push the data to the products array
      // selectedBox.products.push(updatedProductDetails);

      setSelectedBox({
        ...selectedBox,
        products: selectedBox.products,
      });
      setIsSameAsProductId(false);
      // setSelectedProduct({ ...defaultProductState });
    }
    updateFinalChargableWeight(name === 'Cargo Solutions Network');
  }

  function cancelProduct() {
    const boxesTemp = Array.from(boxes);
    const boxIndex = boxesTemp.findIndex(box => box.id === selectedBox.id);
    setBoxes([...boxesTemp]);
    setSelectedBox({
      ...selectedBox,
      products: [...boxesTemp[boxIndex].products],
    });
    setSelectedProduct(null);
    setIsSameAsProductId(false);
  }

  function validateProductData() {
    const _productDetails = { ...productDetails };
    delete _productDetails.isUpdate;
    const result: ValidationResult = getProductDescriptionSchema(
      requireEuComplianceFields,
      isCpscRequired,
      isProductIdRequired,
    ).validate(_productDetails, {
      abortEarly: false,
      stripUnknown: true,
    });
    const { error } = result;
    const errorData: ProductErrorType = {};

    if (error) {
      for (const item of error.details) {
        const name = item.path[0];
        const message = item.message;
        errorData[name as keyof ProductErrorType] = message;
      }
    }

    if (isCpscRequired) {
      const cpscValidation = validateCpscCompliance(productDetails.cpscCompliance, true, true);
      if (!cpscValidation.isValid) {
        Object.assign(errorData, cpscValidation.errors);
      }
    }

    if (isShipmentExport && !productDetails.HSNCode?.trim()) {
      errorData.HSNCode = 'Please enter HSN Code';
    }

    if (Object.keys(errorData).length > 0) {
      setErrors(errorData);
      return;
    }

    if (productDetails.isUpdate) {
      updateProduct();
    } else {
      addProduct();
    }
  }

  // Allow standard keyboard characters + line breaks
  const allowedRegex = new RegExp('^[a-zA-Z0-9 .,!?@#$%^&*()_\\-+=[\\]{}:;"\'<>/|\\\\~`\\n\\r]*$');

  const validateProperty = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const errorData = { ...errors };

    // 🔹 Special character validation only for text fields
    if (
      name === 'description' ||
      name === 'HSNName' ||
      name === 'HSNCode' ||
      name === 'productId' ||
      name === 'merchantProductId' ||
      name === 'standardManufacturerProductId' ||
      name === 'nonStandardManufacturerProductId'
    ) {
      if (!allowedRegex.test(value)) {
        errorData[name as keyof ProductErrorType] = 'Only standard keyboard characters are allowed.';
        setErrors(errorData);
        return;
      }
    }

    // 🔹 Joi Schema Validation
    const obj = { [name]: value };
    const schema = getProductDescriptionSchema(requireEuComplianceFields, isCpscRequired, isProductIdRequired);
    const result = schema.validate(obj);
    const { error } = result;

    const errorMessage = error && error.details[0].context?.key === name ? error.details[0].message : null;

    if (errorMessage && name in defaultProductErrorState) {
      errorData[name as keyof ProductErrorType] = errorMessage;
    } else if (name === 'productId' && isProductIdRequired && !value?.trim()) {
      errorData.productId = 'Please enter Product ID';
    } else if (name === 'productIdType' && isProductIdRequired && !value?.trim()) {
      errorData.productIdType = 'Please select Product ID Type';
    } else if (name === 'HSNCode' && isShipmentExport && !value?.trim()) {
      errorData.HSNCode = 'Please enter HSN Code';
    } else if (name === 'merchantProductId' && requireEuComplianceFields && !value?.trim()) {
      errorData.merchantProductId = 'Please enter Merchant ID (M-PID)';
    } else if (name === 'nonStandardManufacturerProductId' && requireEuComplianceFields && !value?.trim()) {
      errorData.nonStandardManufacturerProductId = 'Please enter Manufacturer part no. (NS-PID)';
    } else {
      errorData[name as keyof ProductErrorType] = '';
    }

    setErrors(errorData);
  };

  function calculateTotalValue() {
    if (quantity && unitValue)
      setProductDetails({
        ...productDetails,
        totalValue: quantity * (typeof unitValue === 'string' ? Number(unitValue) : unitValue),
      });
  }

  function handleEditProduct(data: ProductType) {
    setSelectedProduct({ ...data, isUpdate: true });
  }

  function handleDeleteProduct(ind: number) {
    let productsTemp = structuredClone(selectedBox.products);
    productsTemp.splice(ind, 1);
    const boxesTemp = structuredClone(boxes);
    const boxInd = boxesTemp.findIndex(box => box.id === selectedBox.id);
    productsTemp = productsTemp.map((p, ind) => ({ ...p, id: ind.toString() }));
    boxesTemp[boxInd].products = productsTemp;
    setSelectedBox({ ...selectedBox, products: productsTemp });

    const arr = updateBoxId<BoxType>(boxesTemp, defaultBoxState, 0);
    setBoxes([...arr]);
  }
  useEffect(() => {
    const isShipperValid = shipper.city && shipper.country && shipper.pincode;
    const isConsigneeValid = consignee.city && consignee.country && consignee.pincode;
    if (boxes.length >= 1 && isShipperValid && isConsigneeValid) {
      updateFinalChargableWeight(name === 'Cargo Solutions Network');
    }
  }, [boxes.length]);

  const hasErrors = Object.values(errors).some(error => error);

  const isEuMissing =
    requireEuComplianceFields &&
    (!productDetails.merchantProductId?.trim() || !productDetails.nonStandardManufacturerProductId?.trim());

  const isCpscMissing = isCpscRequired && !validateCpscCompliance(productDetails.cpscCompliance, true, true).isValid;

  const isProductInvalid =
    hasErrors ||
    !productDetails.description ||
    !productDetails.quantity ||
    !productDetails.unitValue ||
    (isShipmentExport && !productDetails.HSNCode?.trim()) ||
    isEuMissing ||
    isCpscMissing;

  const isFormDirtyOrUpdate =
    productDetails.isUpdate ||
    !!(
      productDetails.description ||
      productDetails.quantity ||
      productDetails.unitValue ||
      productDetails.HSNCode ||
      productDetails.productId ||
      productDetails.merchantProductId ||
      productDetails.standardManufacturerProductId ||
      productDetails.nonStandardManufacturerProductId
    );

  const isFormInvalid = isFormDirtyOrUpdate && isProductInvalid;

  useEffect(() => {
    if (setIsProductFormInvalid) {
      setIsProductFormInvalid(isFormInvalid);
    }
  }, [isFormInvalid, setIsProductFormInvalid]);

  useEffect(() => {
    if (setIsHSNValidating) {
      setIsHSNValidating(isLoadingHSN);
    }
  }, [isLoadingHSN, setIsHSNValidating]);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
        bgcolor: theme.palette.background.paper,
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '8px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StraightenOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>Box Dimensions</Typography>
              <Chip
                label={`Box ${parseInt(selectedBox?.id || '0') + 1} of ${boxes.length}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              />
            </Box>
          </Box>
        </Box>

        {!isInitialCreateMode && (
          <SbButton
            variant="outlined"
            size="small"
            sx={{ borderRadius: '8px' }}
            onClick={() => {
              if (shipmentById?.shipmentNumber) {
                setIsEditMode(true);
              }
            }}
            disabled={isDisabled}
          >
            Edit
          </SbButton>
        )}
      </Box>

      {/* Card Body */}
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* Section 1: Box Dimensions */}
        <Box sx={{ mb: 2.5 }}>
          <InputLabel
            sx={{
              mb: '8px',
              color: 'common.black',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
              letterSpacing: '0.3px',
              display: 'block',
            }}
          >
            Package Dimensions ({shipmentType.unit})
          </InputLabel>
          <BoxDimensions writePermission={writePermission} isDisabled={isDisabled || !isEditMode} />
        </Box>

        <Divider sx={{ my: 2.5, borderColor: alpha(theme.palette.divider, 0.1) }} />

        {/* Section 2: Add Product Item */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <InputLabel
              sx={{
                mb: 0,
                color: 'common.black',
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
                letterSpacing: '0.3px',
                display: 'block',
              }}
            >
              {productDetails.isUpdate ? 'Edit Product Item' : 'Add Product Item'}
            </InputLabel>
            {isShipmentExport && (
              <Chip
                label="HSN Code Required for Export"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  color: 'warning.dark',
                }}
              />
            )}
          </Box>
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid item md={12} xs={12}>
              <Grid container columnSpacing={2} rowSpacing={1}>
                <Grid item md={4} xs={12}>
                  <Grid container columnSpacing={2} rowSpacing={1}>
                    <Grid item md={12} xs={12}>
                      <SbTextField
                        required
                        multiline
                        rows={5}
                        placeholder="Enter Description"
                        label="Description"
                        onChange={handleChange}
                        name="description"
                        value={description}
                        error={errors.description}
                        style={{ height: '100%' }}
                        disabled={!writePermission || isDisabled || !isEditMode}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item md={8} xs={12}>
                  <Grid container columnSpacing={2} rowSpacing={1.5}>
                    <Grid item md={6} xs={12}>
                      <SbTextField
                        placeholder="Enter Quantity"
                        label="Quantity"
                        type="integer"
                        onChange={handleChange}
                        name="quantity"
                        value={quantity ? JSON.stringify(quantity) : ''}
                        onBlur={calculateTotalValue}
                        required
                        error={errors.quantity}
                        disabled={!writePermission || isDisabled || !isEditMode}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <SbTextField
                        placeholder={`Unit value in ${shipmentType.currency}`}
                        label="Unit Value"
                        onChange={handleChange}
                        name="unitValue"
                        value={unitValue ? unitValue : ''}
                        onBlur={calculateTotalValue}
                        type="number"
                        required
                        maxDecimals={4}
                        error={errors.unitValue}
                        disabled={!writePermission || isDisabled || !isEditMode}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <SbTextField
                        placeholder="Enter HSN Name"
                        label="HSN Name"
                        onChange={handleChange}
                        name="HSNName"
                        value={HSNName}
                        error={errors.HSNName}
                        disabled={!writePermission || isDisabled || !isEditMode}
                      />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      {productDetails.classifications && productDetails.classifications.length > 0 ? (
                        <Autocomplete
                          freeSolo
                          openOnFocus
                          PopperComponent={ZoomAdjustedPopper}
                          options={productDetails.classifications}
                          getOptionLabel={option => {
                            if (typeof option === 'string') return option;
                            return option.hsnCode || '';
                          }}
                          value={HSNCode || ''}
                          onChange={(_event: any, newValue: any) => {
                            let code = '';
                            if (typeof newValue === 'string') {
                              code = newValue;
                            } else if (newValue && newValue.hsnCode) {
                              code = newValue.hsnCode;
                            }
                            setProductDetails(prev => ({
                              ...prev,
                              HSNCode: code,
                            }));
                            setErrors(prev => ({
                              ...prev,
                              HSNCode: '',
                            }));
                          }}
                          onInputChange={(_event: any, newInputValue: string) => {
                            setProductDetails(prev => ({
                              ...prev,
                              HSNCode: newInputValue,
                            }));
                            const dummyEvent = {
                              target: {
                                name: 'HSNCode',
                                value: newInputValue,
                              },
                            } as React.ChangeEvent<HTMLInputElement>;
                            validateProperty(dummyEvent);
                          }}
                          disabled={!writePermission || isDisabled || !isEditMode}
                          ListboxProps={{
                            style: {
                              padding: 0,
                            },
                          }}
                          renderOption={(props, option) => {
                            const { key, ...optionProps } = props as any;
                            return (
                              <Box
                                component="li"
                                key={key || option.hsnCode + option.hurricaneDescription}
                                {...optionProps}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  width: '100%',
                                  padding: '10px 16px !important',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s ease',
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  },
                                  '&.Mui-selected, &[aria-selected="true"]': {
                                    backgroundColor: 'action.selected',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                      backgroundColor: 'action.focus',
                                    },
                                  },
                                  '&:last-child': {
                                    borderBottom: 'none',
                                  },
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.925rem',
                                    color: 'text.primary',
                                    lineHeight: 1.3,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {option.hurricaneDescription?.toUpperCase() || option.hsnCode?.toUpperCase()}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.8rem',
                                    marginTop: '4px',
                                    fontWeight: 400,
                                  }}
                                >
                                  HSN Code: {option.hsnCode}
                                </Typography>
                              </Box>
                            );
                          }}
                          renderInput={params => {
                            const { InputProps, inputProps, ...restParams } = params;
                            const hasError = !!errors.HSNCode;
                            return (
                              <div className="sb-textfield-wrapper">
                                <InputLabel
                                  className="sb-textfield-label"
                                  required={isShipmentExport}
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    marginBottom: '8px',
                                    display: 'block',
                                    letterSpacing: '0.3px',
                                    color: hasError ? theme.palette.error.main : 'common.black',
                                    textTransform: 'none',
                                    '&.Mui-required .MuiFormLabel-asterisk': {
                                      color: 'inherit',
                                      marginLeft: '4px',
                                    },
                                  }}
                                >
                                  HSN Code
                                </InputLabel>
                                <FormControl className="sb-textfield-control" fullWidth error={hasError}>
                                  <OutlinedInput
                                    {...restParams}
                                    {...InputProps}
                                    inputProps={{
                                      ...inputProps,
                                      placeholder: 'Enter HSN Code...',
                                    }}
                                    error={hasError}
                                    className="sb-textfield-input"
                                    sx={{
                                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                      backdropFilter: 'blur(10px)',
                                      padding: '4px',
                                      borderRadius: '12px',
                                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                      },
                                      border: hasError ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                                      '& .MuiOutlinedInput-input': {
                                        fontSize: '0.95rem',
                                        fontWeight: 400,
                                        padding: '12px 16px',
                                        letterSpacing: '0.3px',
                                        height: 'auto',
                                      },
                                      '&.Mui-focused': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                                        backdropFilter: 'blur(12px)',
                                        border: hasError ? '1px solid #d32f2f' : '1px solid #1976d2',
                                        boxShadow: hasError
                                          ? '0 0 0 2px rgba(211, 47, 47, 0.12)'
                                          : '0 0 0 2px rgba(25, 118, 210, 0.12)',
                                      },
                                      '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        backdropFilter: 'blur(12px)',
                                        borderColor: hasError ? '#d32f2f' : '#1976d2',
                                      },
                                      '&.Mui-disabled': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                        cursor: 'not-allowed',
                                        '& .MuiOutlinedInput-input': {
                                          color: 'text.disabled',
                                          cursor: 'not-allowed',
                                        },
                                      },
                                    }}
                                    disabled={!writePermission || isDisabled || !isEditMode}
                                  />
                                  {hasError && (
                                    <FormHelperText
                                      error
                                      sx={{
                                        margin: '6px 4px 0 4px',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        color: '#d32f2f',
                                        letterSpacing: '0.2px',
                                      }}
                                    >
                                      {errors.HSNCode}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </div>
                            );
                          }}
                        />
                      ) : (
                        <SbTextField
                          required={isShipmentExport}
                          placeholder="Enter HSN Code"
                          label="HSN Code"
                          onChange={handleChange}
                          name="HSNCode"
                          value={HSNCode}
                          error={errors.HSNCode}
                          disabled={!writePermission || isDisabled || !isEditMode}
                        />
                      )}
                      {isShipmentExport && !getHSNCodeFetched && (
                        <SbButton
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1, height: '36px', fontSize: '14px' }}
                          onClick={handleGetHSNCode}
                          disabled={isGetHSNDisabled}
                          loading={isLoadingHSN}
                        >
                          Get HSN Code
                        </SbButton>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 1.5, mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                        }}
                      >
                        Product Identification {isEUConsignee && '& EU Compliance'}{' '}
                        {isUSConsignee && '& US CPSC Compliance'}
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                    </Box>

                    <Grid container columnSpacing={2} rowSpacing={1.5}>
                      <Grid item md={6} xs={12}>
                        <SbTextField
                          required={isProductIdRequired}
                          placeholder="Enter Product ID"
                          label={`Product ID`}
                          onChange={handleChange}
                          name="productId"
                          value={productDetails.productId || ''}
                          error={errors.productId}
                          disabled={!writePermission || isDisabled || !isEditMode}
                        />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <SBDropdown
                          id="productIdType"
                          label={`Product ID Type`}
                          required={isProductIdRequired}
                          name="productIdType"
                          placeholder="Select Product ID Type"
                          options={PRODUCT_ID_TYPE_OPTIONS}
                          value={productDetails.productIdType ? [productDetails.productIdType] : ['SKU']}
                          onChange={selected => {
                            const val = selected[0] || 'SKU';
                            setProductDetails(prev => ({
                              ...prev,
                              productIdType: val,
                            }));
                          }}
                          disabled={!writePermission || isDisabled || !isEditMode}
                          error={errors.productIdType}
                        />
                      </Grid>
                      {isEUConsignee && (
                        <>
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: '8px',
                                bgcolor: alpha(theme.palette.info.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 18 }} />
                              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                                Goods value {shipmentType?.currency || 'INR'} {totalShipmentValue.toFixed(2)}{' '}
                                {eurEquivalentText ? `${eurEquivalentText} ` : ''}· €3 duty per line
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <Box sx={{ width: '100%' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: '8px',
                                  minHeight: '21px',
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    letterSpacing: '0.3px',
                                    color: theme.palette.text.primary,
                                  }}
                                >
                                  Merchant ID (M-PID){' '}
                                  {requireEuComplianceFields && (
                                    <span style={{ color: theme.palette.error.main }}>*</span>
                                  )}
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={isSameAsProductId}
                                      onChange={handleSameAsProductIdChange}
                                      disabled={!writePermission || isDisabled || !isEditMode}
                                      sx={{
                                        p: '2px',
                                        mr: 0.5,
                                        '&.Mui-checked': { color: 'primary.main' },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.78rem',
                                        color: isSameAsProductId ? 'primary.main' : 'text.secondary',
                                        fontWeight: isSameAsProductId ? 600 : 400,
                                        userSelect: 'none',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      Same as Product ID
                                    </Typography>
                                  }
                                  sx={{ m: 0 }}
                                />
                              </Box>
                              <SbTextField
                                required={requireEuComplianceFields}
                                placeholder={isSameAsProductId ? 'Synced with Product ID' : 'Enter Merchant ID (M-PID)'}
                                onChange={handleChange}
                                name="merchantProductId"
                                value={productDetails.merchantProductId || ''}
                                error={errors.merchantProductId}
                                disabled={isSameAsProductId || !writePermission || isDisabled || !isEditMode}
                              />
                            </Box>
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <SbTextField
                              placeholder="Enter Barcode (S-PID)"
                              label="Barcode (S-PID)"
                              onChange={handleChange}
                              name="standardManufacturerProductId"
                              value={
                                productDetails.standardManufacturerProductId === 'NO'
                                  ? ''
                                  : productDetails.standardManufacturerProductId || ''
                              }
                              error={errors.standardManufacturerProductId}
                              disabled={!writePermission || isDisabled || !isEditMode}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <SbTextField
                              required={requireEuComplianceFields}
                              placeholder="Enter Manufacturer part no. (NS-PID)"
                              label={`Manufacturer part no. (NS-PID)${requireEuComplianceFields ? '' : ''}`}
                              onChange={handleChange}
                              name="nonStandardManufacturerProductId"
                              value={productDetails.nonStandardManufacturerProductId || ''}
                              error={errors.nonStandardManufacturerProductId}
                              disabled={!writePermission || isDisabled || !isEditMode}
                            />
                          </Grid>
                        </>
                      )}

                      {isUSConsignee && (
                        <>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.8px',
                                  color: 'primary.main',
                                  fontSize: '0.75rem',
                                }}
                              >
                                US CPSC Compliance
                              </Typography>
                              <Box
                                sx={{
                                  flex: 1,
                                  height: '1px',
                                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                  ml: 1.5,
                                }}
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ mb: 1 }}>
                              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1 }}>
                                Is this item being regulated?{isCpscRequired ? ' *' : ''}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  p: 0.5,
                                  bgcolor: alpha(theme.palette.divider, 0.08),
                                  borderRadius: '10px',
                                  gap: 1,
                                }}
                              >
                                <SbButton
                                  variant={productDetails.cpscCompliance?.beingSold ? 'contained' : 'text'}
                                  onClick={() => handleCpscBeingSoldChange(true)}
                                  disabled={!writePermission || isDisabled || !isEditMode}
                                  sx={{
                                    px: 3,
                                    py: 0.75,
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    bgcolor: productDetails.cpscCompliance?.beingSold ? 'primary.main' : 'transparent',
                                    color: productDetails.cpscCompliance?.beingSold ? 'common.white' : 'text.primary',
                                    boxShadow: productDetails.cpscCompliance?.beingSold
                                      ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                      : 'none',
                                    '&:hover': {
                                      bgcolor: productDetails.cpscCompliance?.beingSold
                                        ? 'primary.dark'
                                        : alpha(theme.palette.action.hover, 0.1),
                                    },
                                  }}
                                >
                                  Yes
                                </SbButton>
                                <SbButton
                                  variant={!productDetails.cpscCompliance?.beingSold ? 'contained' : 'text'}
                                  onClick={() => handleCpscBeingSoldChange(false)}
                                  disabled={!writePermission || isDisabled || !isEditMode}
                                  sx={{
                                    px: 3,
                                    py: 0.75,
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    bgcolor: !productDetails.cpscCompliance?.beingSold ? 'primary.main' : 'transparent',
                                    color: !productDetails.cpscCompliance?.beingSold ? 'common.white' : 'text.primary',
                                    boxShadow: !productDetails.cpscCompliance?.beingSold
                                      ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                      : 'none',
                                    '&:hover': {
                                      bgcolor: !productDetails.cpscCompliance?.beingSold
                                        ? 'primary.dark'
                                        : alpha(theme.palette.action.hover, 0.1),
                                    },
                                  }}
                                >
                                  No
                                </SbButton>
                              </Box>
                              {errors.cpscBeingSold && (
                                <Typography sx={{ color: 'error.main', fontSize: '12px', mt: 0.5 }}>
                                  {errors.cpscBeingSold}
                                </Typography>
                              )}
                            </Box>
                          </Grid>

                          {!productDetails.cpscCompliance?.beingSold && (
                            <>
                              <Grid item md={6} xs={12}>
                                <SBDropdown
                                  id="cpscDisclaimCode"
                                  label="Disclaim Code"
                                  required={isCpscRequired}
                                  name="cpscDisclaimCode"
                                  placeholder="Select Disclaim Code"
                                  options={CPSC_DISCLAIM_CODE_OPTIONS}
                                  value={
                                    productDetails.cpscCompliance?.disclaimCode
                                      ? [normalizeCpscDisclaimCode(productDetails.cpscCompliance.disclaimCode)]
                                      : [CPSC_DEFAULT_DISCLAIM_CODE]
                                  }
                                  onChange={selected => {
                                    const val = selected[0] || CPSC_DEFAULT_DISCLAIM_CODE;
                                    handleCpscFieldChange('disclaimCode', val);
                                  }}
                                  disabled={!writePermission || isDisabled || !isEditMode}
                                  error={errors.cpscDisclaimCode}
                                />
                              </Grid>
                              <Grid item md={6} xs={12}>
                                <SbTextField
                                  placeholder="Enter additional context (optional)"
                                  label="Additional Context"
                                  onChange={e => handleCpscFieldChange('additionalContext', e.target.value)}
                                  name="cpscAdditionalContext"
                                  value={productDetails.cpscCompliance?.additionalContext || ''}
                                  error={errors.cpscAdditionalContext}
                                  disabled={!writePermission || isDisabled || !isEditMode}
                                />
                              </Grid>
                            </>
                          )}

                          {productDetails.cpscCompliance?.beingSold && (
                            <>
                              <Grid item xs={12}>
                                <Box sx={{ width: '100%' }}>
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.95rem',
                                      letterSpacing: '0.3px',
                                      color: theme.palette.text.primary,
                                      mb: 1,
                                    }}
                                  >
                                    Is the certificate filed in the CPSC Product Registry?{' '}
                                    {isCpscRequired && <span style={{ color: theme.palette.error.main }}>*</span>}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      p: '4px',
                                      borderRadius: '10px',
                                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                      gap: 1,
                                    }}
                                  >
                                    <SbButton
                                      variant={
                                        productDetails.cpscCompliance?.certificateFiled === true ? 'contained' : 'text'
                                      }
                                      onClick={() => handleCpscCertificateFiledChange(true)}
                                      disabled={!writePermission || isDisabled || !isEditMode}
                                      sx={{
                                        px: 3,
                                        py: 0.75,
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        bgcolor:
                                          productDetails.cpscCompliance?.certificateFiled === true
                                            ? 'primary.main'
                                            : 'transparent',
                                        color:
                                          productDetails.cpscCompliance?.certificateFiled === true
                                            ? 'common.white'
                                            : 'text.primary',
                                        boxShadow:
                                          productDetails.cpscCompliance?.certificateFiled === true
                                            ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                            : 'none',
                                        '&:hover': {
                                          bgcolor:
                                            productDetails.cpscCompliance?.certificateFiled === true
                                              ? 'primary.dark'
                                              : alpha(theme.palette.action.hover, 0.1),
                                        },
                                      }}
                                    >
                                      Yes
                                    </SbButton>
                                    <SbButton
                                      variant={
                                        productDetails.cpscCompliance?.certificateFiled === false ? 'contained' : 'text'
                                      }
                                      onClick={() => handleCpscCertificateFiledChange(false)}
                                      disabled={!writePermission || isDisabled || !isEditMode}
                                      sx={{
                                        px: 3,
                                        py: 0.75,
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        bgcolor:
                                          productDetails.cpscCompliance?.certificateFiled === false
                                            ? 'primary.main'
                                            : 'transparent',
                                        color:
                                          productDetails.cpscCompliance?.certificateFiled === false
                                            ? 'common.white'
                                            : 'text.primary',
                                        boxShadow:
                                          productDetails.cpscCompliance?.certificateFiled === false
                                            ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                            : 'none',
                                        '&:hover': {
                                          bgcolor:
                                            productDetails.cpscCompliance?.certificateFiled === false
                                              ? 'primary.dark'
                                              : alpha(theme.palette.action.hover, 0.1),
                                        },
                                      }}
                                    >
                                      No
                                    </SbButton>
                                  </Box>
                                  {errors.cpscCertificateFiled && (
                                    <Typography sx={{ color: 'error.main', fontSize: '12px', mt: 0.5 }}>
                                      {errors.cpscCertificateFiled}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>

                              {productDetails.cpscCompliance?.certificateFiled === true && (
                                <>
                                  <Grid item md={4} xs={12}>
                                    <SbTextField
                                      required={isCpscRequired}
                                      placeholder="Enter Product ID (registry)"
                                      label="Product ID (registry)"
                                      onChange={e => handleCpscFieldChange('productIdRegistry', e.target.value)}
                                      name="cpscProductIdRegistry"
                                      value={productDetails.cpscCompliance?.productIdRegistry || ''}
                                      error={errors.cpscProductIdRegistry}
                                      disabled={!writePermission || isDisabled || !isEditMode}
                                    />
                                  </Grid>
                                  <Grid item md={4} xs={12}>
                                    <SbTextField
                                      required={isCpscRequired}
                                      placeholder="Enter Certificate Version ID"
                                      label="Certificate Version ID"
                                      onChange={e => handleCpscFieldChange('certificateVersionId', e.target.value)}
                                      name="cpscCertificateVersionId"
                                      value={productDetails.cpscCompliance?.certificateVersionId || ''}
                                      error={errors.cpscCertificateVersionId}
                                      disabled={!writePermission || isDisabled || !isEditMode}
                                    />
                                  </Grid>
                                  <Grid item md={4} xs={12}>
                                    <SbTextField
                                      required={isCpscRequired}
                                      placeholder="Enter Certifier ID"
                                      label="Certifier ID"
                                      onChange={e => handleCpscFieldChange('certifierId', e.target.value)}
                                      name="cpscCertifierId"
                                      value={productDetails.cpscCompliance?.certifierId || ''}
                                      error={errors.cpscCertifierId}
                                      disabled={!writePermission || isDisabled || !isEditMode}
                                    />
                                  </Grid>
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </Grid>
                  </Box>
                </Grid>
                <Grid container item xs={12} columnSpacing={1} justifyContent={'end'} className="mt-1">
                  {productDetails.isUpdate && (
                    <Grid item>
                      <SbButton fullWidth variant="outlined" onClick={cancelProduct}>
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                  <Grid item>
                    <SbButton
                      fullWidth
                      variant="contained"
                      onClick={validateProductData}
                      disabled={!writePermission || isDisabled || !isEditMode || isProductInvalid}
                    >
                      {productDetails.isUpdate ? 'Update' : 'Add'}
                    </SbButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        {selectedBox?.products?.length ? (
          <>
            <Divider sx={{ my: 2.5, borderColor: alpha(theme.palette.divider, 0.1) }} />
            <ProductTable
              title="Declared Goods"
              enableActions
              products={selectedBox.products}
              handleEdit={handleEditProduct}
              handleDelete={handleDeleteProduct}
              selectedProduct={selectedProduct}
              destinationCountry={consignee?.country}
              isEU={isEUConsignee}
              isUS={isUSConsignee}
            />
          </>
        ) : (
          <></>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductDescription;
